import React, { useState, useCallback, useRef } from 'react';
import { GeneralParametersSection } from './components/parameters/GeneralParametersSection';
import { PersonalParametersSection } from './components/parameters/PersonalParametersSection';
import { SimulationDisplay } from './components/simulation/SimulationDisplay';
import { ResultsDisplay } from './components/results/ResultsDisplay';
import { SimulationEngine } from './core/SimulationEngine';
import { PersonData, TrialResult, CapturedData, HistogramConfig } from './types';
import { generalParametersData, personalParametersData, createDefaultCareer } from './data/parameterDefinitions';
import { exportParameters, importParameters, validateGeneralParameters, validatePersonalParameters } from './utils/exportImport';
import './App.css';
import './components/common/Tooltip.css';

/**
 * Main application component
 */
function App() {

  const createPersonData = (index: number): PersonData => ({
    id: `person${index + 1}`,
    name: `Person ${index + 1}`,
    parameterValues: {
      birthdate: { value: '1985-06-15', type: 'date' },
      retirementAge: { value: 67, type: 'number' },
      socialSecurityMonthlyBenefit: { value: 1500, type: 'number' },
      lifeExpectancy: { mean: 85, stdDev: 15, type: 'meanStdDev' as const }
    },
    careers: [createDefaultCareer()]
  });

  // General parameters state
  const [generalValues, setGeneralValues] = useState<Record<string, any>>(() => {
    const initial: Record<string, any> = {};
    generalParametersData.forEach(param => {
      if (param.type === 'date') {
        initial[param.id] = {
          value: param.defaultValue || '',
          type: 'date' as const
        };
      } else if (param.type === 'meanStdDev') {
        initial[param.id] = {
          mean: param.defaultMean || 0,
          stdDev: param.defaultStdDev || 0,
          type: 'meanStdDev' as const
        };
      }
    });
    return initial;
  });

  // Personal parameters state
  const [people, setPeople] = useState<PersonData[]>(() => [
    createPersonData(0),
  ]);

  // Simulation state
  const [isSimulationRunning, setIsSimulationRunning] = useState(false);
  const [simulationProgress, setSimulationProgress] = useState({ completed: 0, total: 0 });
  const [currentTrialResults, setCurrentTrialResults] = useState<TrialResult[]>([]);
  const [finalResults, setFinalResults] = useState<TrialResult[]>([]);
  const [showResults, setShowResults] = useState(false);

  // Simulation engine reference
  const simulationEngineRef = useRef<SimulationEngine>(new SimulationEngine());

  // App explanation collapse state
  const [isExplanationCollapsed, setIsExplanationCollapsed] = useState(true);

  // Histogram configuration
  const histogramConfig: HistogramConfig = {
    binSize: 50000,
    maxExpectedValue: 5000000,
    numBins: Math.ceil(5000000 / 50000),
    updateFrequency: Math.max(1, Math.floor(1000 / 100))
  };

  // General parameter handlers
  const handleGeneralParameterChange = useCallback((id: string, value: any) => {
    setGeneralValues(prev => {
      const paramDef = generalParametersData.find(p => p.id === id);
      if (!paramDef) return prev;

      let newValue;
      if (paramDef.type === 'date') {
        newValue = {
          value,
          type: 'date' as const
        };
      } else if (paramDef.type === 'meanStdDev') {
        newValue = {
          ...value,
          type: 'meanStdDev' as const
        };
      } else {
        newValue = value;
      }

      return {
        ...prev,
        [id]: newValue
      };
    });
  }, []);

  // Person management handlers
  const handlePersonChange = useCallback((personId: string, field: string, value: any) => {
    setPeople(prev => prev.map(person => {
      if (person.id !== personId) return person;

      if (field === 'name') {
        return { ...person, name: value };
      } else if (field.startsWith('parameterValues.')) {
        const paramId = field.replace('parameterValues.', '');
        return {
          ...person,
          parameterValues: {
            ...person.parameterValues,
            [paramId]: value
          }
        };
      }

      return person;
    }));
  }, []);

  const handlePersonDelete = useCallback((personId: string) => {
    if (people.length <= 1) {
      console.warn('Cannot delete the last person');
      return;
    }
    setPeople(prev => prev.filter(p => p.id !== personId));
  }, [people.length]);

  const handlePersonAdd = useCallback(() => {
    if (people.length >= 2) {
      console.warn('Maximum number of people (2) reached');
      return;
    }

    const newPerson = createPersonData(people.length);

    setPeople(prev => [...prev, newPerson]);
  }, [people.length]);

  // Career management handlers
  const handleCareerChange = useCallback((personId: string, careerId: string, field: string, value: any) => {
    setPeople(prev => prev.map(person => {
      if (person.id !== personId) return person;

      return {
        ...person,
        careers: person.careers.map(career => {
          if (career.id !== careerId) return career;
          return { ...career, [field]: value };
        })
      };
    }));
  }, []);

  const handleCareerDelete = useCallback((personId: string, careerId: string) => {
    setPeople(prev => prev.map(person => {
      if (person.id !== personId) return person;

      // Allow deletion of all careers - person can have zero careers
      return {
        ...person,
        careers: person.careers.filter(c => c.id !== careerId)
      };
    }));
  }, []);

  const handleCareerAdd = useCallback((personId: string) => {
    setPeople(prev => prev.map(person => {
      if (person.id !== personId) return person;

      if (person.careers.length >= 5) {
        console.warn('Maximum number of careers (5) reached');
        return person;
      }

      return {
        ...person,
        careers: [...person.careers, createDefaultCareer()]
      };
    }));
  }, []);

  // Export/Import handlers
  const handleExport = useCallback(() => {
    // Convert structured general values back to raw format for export
    const rawGeneralValues: Record<string, any> = {};
    Object.keys(generalValues).forEach(key => {
      const param = generalValues[key];
      if (param && typeof param === 'object') {
        if ('value' in param) {
          rawGeneralValues[key] = param.value;
        } else if ('mean' in param && 'stdDev' in param) {
          rawGeneralValues[key] = {
            mean: param.mean,
            stdDev: param.stdDev
          };
        } else {
          rawGeneralValues[key] = param;
        }
      } else {
        rawGeneralValues[key] = param;
      }
    });
    
    exportParameters(rawGeneralValues, people);
  }, [generalValues, people]);

  const handleImport = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    importParameters(
      file,
      (importedGeneralValues, importedPeople) => {
        // Validate and set imported data
        const validatedGeneral = validateGeneralParameters(importedGeneralValues, generalParametersData);
        const validatedPeople = validatePersonalParameters(importedPeople, personalParametersData);

        // Convert validated general parameters to the expected structure
        const structuredGeneral: Record<string, any> = {};
        generalParametersData.forEach(param => {
          const validatedValue = validatedGeneral[param.id];
          if (param.type === 'date') {
            structuredGeneral[param.id] = {
              value: validatedValue || param.defaultValue || '',
              type: 'date' as const
            };
          } else if (param.type === 'meanStdDev') {
            structuredGeneral[param.id] = {
              mean: validatedValue?.mean || param.defaultMean || 0,
              stdDev: validatedValue?.stdDev || param.defaultStdDev || 0,
              type: 'meanStdDev' as const
            };
          }
        });

        setGeneralValues(structuredGeneral);
        setPeople(validatedPeople);

        console.log('Parameters imported successfully');
      },
      (error) => {
        console.error('Import failed:', error);
      }
    );

    // Clear the input value so the same file can be selected again
    event.target.value = '';
  }, []);

  // Simulation handlers
  const handleRunSimulation = useCallback(async () => {
    try {
      setIsSimulationRunning(true);
      setCurrentTrialResults([]);
      setFinalResults([]);
      setShowResults(false);
      setSimulationProgress({ completed: 0, total: 1000 });

      // Prepare captured data for simulation
      // Ensure all general parameters are present, including newly added ones
      const completeGeneralValues: Record<string, any> = { ...generalValues };
      generalParametersData.forEach(param => {
        if (!completeGeneralValues[param.id]) {
          if (param.type === 'date') {
            completeGeneralValues[param.id] = {
              value: param.defaultValue || '',
              type: 'date' as const
            };
          } else if (param.type === 'meanStdDev') {
            completeGeneralValues[param.id] = {
              mean: param.defaultMean || 0,
              stdDev: param.defaultStdDev || 0,
              type: 'meanStdDev' as const
            };
          }
        }
      });
      
      const capturedData: CapturedData = {
        general: completeGeneralValues,
        personal: {
          people: people.map(p => ({ id: p.id, name: p.name })),
          parameterValues: {}
        }
      };

      people.forEach(person => {
        // Ensure all required personal parameters exist with proper structure
        const validatedParameterValues = { ...person.parameterValues };
        
        // Validate required parameters exist
        personalParametersData.forEach(paramDef => {
          if (paramDef.type !== 'calculated' && !validatedParameterValues[paramDef.id]) {
            // Add missing parameter with default value
            if (paramDef.type === 'date') {
              validatedParameterValues[paramDef.id] = {
                value: paramDef.defaultValue || new Date().toISOString().split('T')[0],
                type: 'date' as const
              };
            } else if (paramDef.type === 'number') {
              validatedParameterValues[paramDef.id] = {
                value: paramDef.defaultValue || 0,
                type: 'number' as const
              };
            } else if (paramDef.type === 'meanStdDev') {
              validatedParameterValues[paramDef.id] = {
                mean: paramDef.defaultMean || 0,
                stdDev: paramDef.defaultStdDev || 0,
                type: 'meanStdDev' as const
              };
            }
            console.warn(`Missing parameter ${paramDef.id} for person ${person.id}, using default value`);
          }
        });
        
        capturedData.personal.parameterValues[person.id] = {
          parameterValues: validatedParameterValues,
          careers: [...person.careers]
        };
      });

      // Run simulation
      const results = await simulationEngineRef.current.runSimulation(
        capturedData,
        (result, completed, total) => {
          setCurrentTrialResults(prev => [...prev, result]);
          setSimulationProgress({ completed, total });
        }
      );

      setFinalResults(results);
      setShowResults(true);
      console.log(`Simulation completed: ${results.length} trials`);

    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.log('Simulation aborted by user');
      } else {
        console.error('Simulation failed:', error);
        alert(`Simulation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    } finally {
      setIsSimulationRunning(false);
    }
  }, [generalValues, people]);

  const handleAbortSimulation = useCallback(() => {
    simulationEngineRef.current.abort();
  }, []);

  const peopleNames = people.map(p => p.name);

  return (
    <div className="App">
      <header className="app-header">
        <div className="app-titling">
          <h1>Will I Have Enough?</h1>
          <p>A Monte Carlo simulation for personal finance planning</p>
        </div>
        <div 
          className={`app-explanation ${isExplanationCollapsed ? 'collapsed' : ''}`}
          onClick={() => setIsExplanationCollapsed(!isExplanationCollapsed)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsExplanationCollapsed(!isExplanationCollapsed);
            }
          }}
        >
          <div className="app-explanation-content">
            <p>This is a financial planning tool. It can help you determine if and when you will have enough money to retire on.
          It resembles some of the tools used by financial planners but puts all the numbers under your own fingertips.
          You enter your financial situation, your planned career(s), and make guesses about how financial variables
          (like inflation or your investment returns) may change in the future, and the simulator will tell you how likely
            it is that your assumptions and plans will lead to positive or negative financial outcomes.</p>
          <p>
            The tool works by running what's called a "Monte Carlo simulation." Your computer takes all the numbers and projects
            what may happen year by year until you die. (All of this happens privately on your computer. No data whatsoever is uploaded anywhere.)
            We pick random numbers from the ranges you specify
            in order to decide what a value for a given year might be&mdash;for example, the inflation rate for that year
            or how much your salary goes up. The main question we want to answer is whether you will still have money
            in your retirement accounts at the end of your life or whether you'll run out of money too soon.
            We want to have at least a little left over when we die!
          </p>
          <p>
            We run this simulation one thousand times, making new, random predictions each time.
            In the end, the simulator doesn't tell you, "Yes, you're fine," or "No, you're going to run out of money."
            Instead it tells you how often, among those 1,000 trial runs, the simulation showed you having enough
            money or else running out. If the simulator predicts that you're going to run out of money in
            80% of potential futures, then you'll need to make adjustments. If it predicts that you'll
            have enough money in 80% of potential futures, then assuming the numbers you've entered are realistic,
            you can take more confidence in your financial plan.
          </p>
          <p>
            Be aware: THIS TOOL MAY BE COMPLETELY WRONG. I am not a financial professional. The tool probably has bugs.
            The calculations may be incorrect! (If you find a bug, please <a href="mailto:web@jeffwofford.com">let me know</a>.) Even if the tool were perfect,
            its predictions are only as good as the assumptions you've entered. Please don't come away from a disappointing
            prediction and think, "I'm doomed." Seek professional advice. And please don't come away from a 
            positive prediction and think, "I've got nothing to worry about." Always be aware: this tool could mislead you.
          </p>
          <p>When entering your numbers, most fields will let you specify both the "mean" and the "std dev." The mean is the average:
            what this value is most likely to be across all trials and years. "Std dev" is short for "standard deviation."
            The standard deviation is a measure of how far the values will wander from the mean. A standard deviation of zero
            means that the value will always be exactly the mean: if you specify, for example, that the inflation rate will be 3%
            and the standard deviation is zero, then every single year will have a fixed inflation rate of 3%.
            The standard deviation says how far away from the mean the values may spread. If you say that the standard deviation
            for inflation is 1, then most years will have an inflation rate between 2% and 4%. Some years may still
            have an inflation rate higher or lower than that, though much higher and much lower values become less and less likely.
            The tool tries to give decent defaults for the mean and standard deviation, so if you're confused
            and want to leave the standard deviation alone, that's fine. In general, the more uncertain you are
            about what a number may end up being in the future, or the more that you know it is likely to waver around,
            the higher your standard deviation should be.
          </p>
          <p>
            I built this tool for my own use and personally I've found it helpful in thinking through the various factors
            in personal financial planning and in understanding the ramifications of different variables and decisions.
            I offer it for others to use, WITHOUT GUARANTEES, USING IT AT YOUR OWN RISK, in the hopes you might find it
            useful too.
          </p>
          </div>
        </div>
      </header>

      <main className="app-main">
        {/* Import/Export Controls */}
        <div className="import-export-controls">
          <div className="parameter-actions">
            <button
              type="button"
              onClick={handleExport}
              className="header-action-btn export-btn"
              disabled={isSimulationRunning}
            >
              Export
            </button>
            
            <label className="header-action-btn import-btn">
              Import
              <input
                type="file"
                accept=".json"
                onChange={handleImport}
                style={{ display: 'none' }}
                disabled={isSimulationRunning}
              />
            </label>
          </div>
        </div>

        {/* General Parameters Section */}
        <GeneralParametersSection
          generalParameters={generalParametersData}
          generalValues={generalValues}
          onGeneralParameterChange={handleGeneralParameterChange}
          disabled={isSimulationRunning}
        />

        {/* Personal Parameters Section */}
        <PersonalParametersSection
          people={people}
          personalParameters={personalParametersData}
          onPersonChange={handlePersonChange}
          onPersonDelete={handlePersonDelete}
          onPersonAdd={handlePersonAdd}
          onCareerChange={handleCareerChange}
          onCareerDelete={handleCareerDelete}
          onCareerAdd={handleCareerAdd}
          simStartDate={(() => {
            const simStartDateParam = generalValues['sim-start-date'];
            return (simStartDateParam && typeof simStartDateParam === 'object' && 'value' in simStartDateParam) 
              ? simStartDateParam.value as string 
              : new Date().toISOString().split('T')[0];
          })()}
          disabled={isSimulationRunning}
        />

        {/* Run Simulation Button */}
        <div className="standalone-simulation-controls">
          <button
            type="button"
            onClick={handleRunSimulation}
            className="run-simulation-btn"
            disabled={isSimulationRunning}
          >
            {isSimulationRunning ? 'Running Simulation...' : 'Run Simulation'}
          </button>
        </div>

        {/* Simulation Display */}
        <SimulationDisplay
          isRunning={isSimulationRunning}
          progress={simulationProgress}
          currentTrialResults={currentTrialResults}
          histogramConfig={histogramConfig}
          peopleNames={peopleNames}
          onAbort={handleAbortSimulation}
        />

        {/* Results Display */}
        <ResultsDisplay
          results={finalResults}
          isVisible={showResults}
        />
      </main>

      <footer className="app-footer">
        <p>&copy; 2025 Jeff Wofford</p>
      </footer>
    </div>
  );
}

export default App;